import React, { useState, useContext } from 'react';
import { AppContext } from '../../context/AppContext';

// --- Hardcoded Dictionaries ---
const professionalTaxSlabs = {
  "Maharashtra": { limit1: 7500, tax1: 0, limit2: 10000, tax2: 175, limit3: Infinity, tax3: 200 },
  "Karnataka": { limit1: 24999, tax1: 0, limit2: Infinity, tax2: 200 },
  "West Bengal": { limit1: 10000, tax1: 0, limit2: 15000, tax2: 110, limit3: 25000, tax3: 130, limit4: 40000, tax4: 150, limit5: Infinity, tax5: 200 },
  "Tamil Nadu": { limit1: 21000, tax1: 0, limit2: Infinity, tax2: 200 },
  "Delhi": { limit1: Infinity, tax1: 0 },
  "Haryana": { limit1: Infinity, tax1: 0 },
  "Others": { limit1: Infinity, tax1: 0 }
};

const lwfSlabs = {
  "Haryana": { employee: 34, employer: 68 },
  "Karnataka": { employee: 4.16, employer: 8.33 },
  "Maharashtra": { employee: 4.16, employer: 12.5 },
  "Tamil Nadu": { employee: 1.66, employer: 3.33 },
  "Delhi": { employee: 0.12, employer: 0.37 },
  "Others": { employee: 0, employer: 0 }
};

const TermTooltip = ({ title, text, position = 'bottom' }) => {
  const [show, setShow] = useState(false);
  const posStyle = position === 'bottom' ? { top: '120%', left: '0' } : { bottom: '120%', left: '0' };
  return (
    <span
      className="position-relative"
      style={{ textDecoration: 'underline dotted', cursor: 'help', color: '#53b7e8' }}
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
    >
      {title}
      {show && (
        <div
          className="position-absolute bg-dark text-white p-3 rounded shadow-lg text-start"
          style={{ ...posStyle, width: '320px', zIndex: 1050, fontSize: '0.8rem', pointerEvents: 'none', fontWeight: 'normal', lineHeight: '1.5' }}
        >
          {text}
        </div>
      )}
    </span>
  );
};

const SalaryStructureWizard = () => {
  const { sidebar } = useContext(AppContext);

  // --- Wizard State ---
  const [currentStep, setCurrentStep] = useState(1);
  const [highestStep, setHighestStep] = useState(1);
  const totalSteps = 4;

  // --- Section A States ---
  const [ctc, setCtc] = useState(1200000);
  const [workState, setWorkState] = useState("Maharashtra");
  const [cityType, setCityType] = useState("Metro");
  const [age, setAge] = useState(30);
  const [isDisabled, setIsDisabled] = useState(false);
  const [pfPolicy, setPfPolicy] = useState("Cap at 15000");
  const [statutoryBonusPct, setStatutoryBonusPct] = useState(8.33);

  // --- Section B States (Optimization) ---
  const [bonus, setBonus] = useState(0);
  const [esop, setEsop] = useState(0);
  const [giftVoucher, setGiftVoucher] = useState(0);
  const [mealVoucher, setMealVoucher] = useState(0);
  const [flexFund, setFlexFund] = useState(0);

  // --- Core Business Logic ---
  const totalRewardsValue = Number(bonus) + Number(esop) + Number(giftVoucher) + Number(mealVoucher) + Number(flexFund);
  const coreAnnualCtc = Math.max(0, Number(ctc) - totalRewardsValue);
  const targetMonthlyCoreCtc = coreAnnualCtc / 12;

  const basic = targetMonthlyCoreCtc * 0.5;

  let employerPF = 0;
  if (pfPolicy === "Cap at 15000") {
    employerPF = basic > 15000 ? 1800 : basic * 0.12;
  } else {
    employerPF = basic * 0.12;
  }
  const edli = Math.min(basic * 0.01, 150);
  const gratuity = basic * 0.0481;
  const employerLWF = lwfSlabs[workState]?.employer || 0;

  let statutoryBonusMonthly = 0;
  if (basic <= 21000) {
    const safePct = Math.min(20, Math.max(8.33, Number(statutoryBonusPct)));
    statutoryBonusMonthly = basic * (safePct / 100);
  }

  let grossSalary = targetMonthlyCoreCtc - (employerPF + edli + gratuity + employerLWF + statutoryBonusMonthly);
  const esiLimit = isDisabled ? 25000 : 21000;
  let employerESI = 0;
  let employeeESI = 0;
  if (grossSalary <= esiLimit) {
    employerESI = grossSalary * 0.0325;
    employeeESI = grossSalary * 0.0075;
    grossSalary = grossSalary - employerESI;
  }

  const hra = cityType === "Metro" ? basic * 0.5 : basic * 0.4;
  const specialAllowance = Math.max(0, grossSalary - (basic + hra));

  const totalNonBasicMonthly = hra + specialAllowance + (totalRewardsValue / 12);
  const isOver50Percent = totalNonBasicMonthly > basic;

  const employeePF = employerPF;
  const employeeLWF = lwfSlabs[workState]?.employee || 0;

  let pt = 0;
  if (workState === "Maharashtra" && age >= 65) {
    pt = 0;
  } else {
    const statePT = professionalTaxSlabs[workState] || professionalTaxSlabs["Others"];
    if (grossSalary <= statePT.limit1) pt = statePT.tax1;
    else if (statePT.limit2 && grossSalary <= statePT.limit2) pt = statePT.tax2;
    else if (statePT.limit3 && grossSalary <= statePT.limit3) pt = statePT.tax3;
    else if (statePT.limit4 && grossSalary <= statePT.limit4) pt = statePT.tax4;
    else if (statePT.limit5 && grossSalary <= statePT.limit5) pt = statePT.tax5;
    else pt = statePT.tax2 || 0;
  }

  let annualIncome = (grossSalary * 12) + totalRewardsValue;
  let netTaxable = Math.max(0, annualIncome - 75000);
  let annualTax = 0;
  if (netTaxable > 2400000) annualTax += (netTaxable - 2400000) * 0.30 + 300000;
  else if (netTaxable > 2000000) annualTax += (netTaxable - 2000000) * 0.25 + 200000;
  else if (netTaxable > 1600000) annualTax += (netTaxable - 1600000) * 0.20 + 120000;
  else if (netTaxable > 1200000) annualTax += (netTaxable - 1200000) * 0.15 + 60000;
  else if (netTaxable > 800000) annualTax += (netTaxable - 800000) * 0.10 + 20000;
  else if (netTaxable > 400000) annualTax += (netTaxable - 400000) * 0.05;
  if (netTaxable <= 1200000) annualTax = 0;
  annualTax = annualTax * 1.04;
  const monthlyTDS = annualTax / 12;

  const netSalary = grossSalary - (employeePF + employeeESI + pt + employeeLWF + monthlyTDS);
  const hypotheticalExtraBasic = (totalRewardsValue / 12) * 0.5;
  const savedStatutory = hypotheticalExtraBasic * (0.12 + 0.0481);

  // --- Wizard Navigation ---
  const nextStep = () => {
    const next = Math.min(currentStep + 1, totalSteps);
    setCurrentStep(next);
    setHighestStep(prev => Math.max(prev, next));
  };
  const prevStep = () => setCurrentStep(prev => Math.max(prev - 1, 1));
  const resetWizard = () => {
    setCtc(1200000); setBonus(0); setEsop(0); setGiftVoucher(0); setMealVoucher(0); setFlexFund(0);
    setCurrentStep(1);
    setHighestStep(1);
  };

  const stepLabels = ["Employee Details", "Statutory Rules", "CTC Optimization", "Final Payslip"];

  return (
    <div className={sidebar ? "taskMain" : "cx-active taskMain"} id="cx-main">
      <div className="role-content">

        {/* Page Header */}
        <div className="sub-header-role">
          <h6 className="mt-2">2026 Salary Structure Setup Wizard</h6>
        </div>

        <div className="innerRoleSec">

          {/* Stepper */}
          <div className="d-flex justify-content-between align-items-center mb-4 position-relative">
            <div
              className="position-absolute"
              style={{ height: '2px', backgroundColor: '#e0e0e0', top: '18px', left: '5%', right: '5%', zIndex: 0 }}
            >
              <div
                style={{
                  height: '100%',
                  backgroundColor: '#53b7e8',
                  width: `${((currentStep - 1) / (totalSteps - 1)) * 100}%`,
                  transition: 'width 0.3s ease'
                }}
              />
            </div>
            {[1, 2, 3, 4].map((stepNum) => {
              const isClickable = stepNum <= highestStep;
              const isDone = stepNum < currentStep;
              const isActive = stepNum === currentStep;
              return (
                <div
                  key={stepNum}
                  className="d-flex flex-column align-items-center"
                  style={{ zIndex: 1, cursor: isClickable ? 'pointer' : 'default', flex: 1 }}
                  onClick={() => isClickable && setCurrentStep(stepNum)}
                >
                  <div
                    style={{
                      width: '36px', height: '36px', borderRadius: '50%',
                      backgroundColor: isDone ? '#53b7e8' : isActive ? '#53b7e8' : '#e0e0e0',
                      color: isDone || isActive ? '#fff' : '#888',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontWeight: '600', fontSize: '0.85rem',
                      border: isActive ? '3px solid #53b7e8' : '3px solid transparent',
                      transition: 'all 0.3s',
                      boxShadow: isActive ? '0 0 0 3px rgba(87, 193, 225, 0.15)' : 'none',
                      backgroundColor: isDone ? '#53b7e8' : isActive ? '#53b7e8' : '#e0e0e0',
                    }}
                  >
                    {isDone ? '✓' : stepNum}
                  </div>
                  <small
                    className="mt-1 text-center d-none d-md-block"
                    style={{ fontSize: '0.72rem', fontWeight: isActive ? '600' : '400', color: isActive ? '#53b7e8' : isClickable ? '#333' : '#aaa' }}
                  >
                    {stepLabels[stepNum - 1]}
                  </small>
                </div>
              );
            })}
          </div>

          {/* ── STEP 1: Employee Details ── */}
          {currentStep === 1 && (
            <div>
              <p className="text-muted mb-4" style={{ fontSize: '0.875rem' }}>
                Establish the baseline. These details dictate regional compliances like Professional Tax, Labour Welfare Funds, and ESI limits.
              </p>
              <div className="row">
                <div className="col-lg-6 col-md-12 mb-4">
                  <label className="enter-role-text">Target Annual CTC (₹)</label>
                  <input
                    type="number"
                    className="date-label-input tasKCategory1"
                    style={{ fontSize: '14px', height: '38px', border: '1px solid #ced4da' }}
                    value={ctc}
                    onChange={(e) => setCtc(e.target.value)}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-lg-4 col-md-6 mb-4">
                  <label className="enter-role-text">Work State</label>
                  <select
                    className="form-select tasKCategory1 select-drop-icon"
                    value={workState}
                    onChange={(e) => setWorkState(e.target.value)}
                  >
                    {Object.keys(professionalTaxSlabs).map(state => (
                      <option key={state} value={state}>{state}</option>
                    ))}
                  </select>
                </div>
                <div className="col-lg-4 col-md-6 mb-4">
                  <label className="enter-role-text">City Type</label>
                  <select
                    className="form-select tasKCategory1 select-drop-icon"
                    value={cityType}
                    onChange={(e) => setCityType(e.target.value)}
                  >
                    <option value="Metro">Metro</option>
                    <option value="Non-Metro">Non-Metro</option>
                  </select>
                </div>
                <div className="col-lg-4 col-md-6 mb-4">
                  <label className="enter-role-text">Employee Age</label>
                  <input
                    type="number"
                    className="date-label-input tasKCategory1"
                    style={{ fontSize: '14px', height: '38px', border: '1px solid #ced4da' }}
                    value={age}
                    onChange={(e) => setAge(e.target.value)}
                  />
                  <small className="text-muted" style={{ fontSize: '0.75rem' }}>Waived for ages 65+ in Maharashtra.</small>
                </div>
              </div>
              <div className="row">
                <div className="col-md-12 mb-2">
                  <div className="form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="pwdCheck"
                      checked={isDisabled}
                      onChange={(e) => setIsDisabled(e.target.checked)}
                    />
                    <label className="form-check-label enter-role-text" htmlFor="pwdCheck">
                      Person with Disability (PwD)
                    </label>
                    <small className="text-muted d-block" style={{ fontSize: '0.75rem' }}>Increases ESI eligibility limit to ₹25,000.</small>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── STEP 2: Statutory Rules ── */}
          {currentStep === 2 && (
            <div>
              <p className="text-muted mb-4" style={{ fontSize: '0.875rem' }}>
                Configure the employer's operational policies regarding Provident Fund and Statutory Bonuses.
              </p>
              <div className="row">
                <div className="col-lg-8 col-md-12 mb-4">
                  <div className="p-3 rounded" style={{ background: '#f8f7ff', border: '1px solid #e2dffe' }}>
                    <label className="enter-role-text">Employer Provident Fund (EPF) Policy</label>
                    <select
                      className="form-select tasKCategory1 select-drop-icon mb-2"
                      value={pfPolicy}
                      onChange={(e) => setPfPolicy(e.target.value)}
                    >
                      <option value="Cap at 15000">Cap at statutory wage limit of ₹15,000</option>
                      <option value="Full Basic">Calculate on full Basic Wage</option>
                    </select>
                    <small className="text-muted" style={{ fontSize: '0.8rem' }}>
                      Limiting PF to the ₹15k wage ceiling maximizes the employee's in-hand salary while remaining legally compliant.
                    </small>
                  </div>
                </div>
                <div className="col-lg-8 col-md-12 mb-4">
                  <div className="p-3 rounded" style={{ background: '#f8f7ff', border: '1px solid #e2dffe' }}>
                    <label className="enter-role-text">Mandatory Statutory Bonus Payout (%)</label>
                    <div className="input-group mb-2" style={{ maxWidth: '200px' }}>
                      <input
                        type="number"
                        className="date-label-input tasKCategory1"
                        style={{ fontSize: '14px', height: '38px', border: '1px solid #ced4da', borderRight: 'none' }}
                        value={statutoryBonusPct}
                        onChange={(e) => setStatutoryBonusPct(e.target.value)}
                        min="8.33" max="20" step="0.01"
                      />
                      <span className="input-group-text bg-white" style={{ height: '38px' }}>%</span>
                    </div>
                    <small className="text-muted" style={{ fontSize: '0.8rem' }}>
                      Legally required for employees with basic wages ≤ ₹21,000/mo. Min 8.33%, Max 20%.
                    </small>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── STEP 3: CTC Optimization ── */}
          {currentStep === 3 && (
            <div>
              <p className="text-muted mb-4" style={{ fontSize: '0.875rem' }}>
                Amounts entered here are legally excluded from the <strong>50% Basic Wage Rule</strong> under the 2026 Codes.
                This prevents artificially inflating your PF and Gratuity liabilities.
              </p>
              <div className="row">
                <div className="col-lg-4 col-md-6 mb-4">
                  <label className="enter-role-text">Performance-Linked Bonus (Annual ₹)</label>
                  <input type="number" className="date-label-input tasKCategory1" style={{ fontSize: '14px', height: '38px', border: '1px solid #ced4da' }} value={bonus} onChange={(e) => setBonus(e.target.value)} />
                  <span className="badge mt-1" style={{ background: '#6c757d', color: '#fff', fontSize: '0.7rem' }}>No statutory cap (Fully Taxable)</span>
                </div>
                <div className="col-lg-4 col-md-6 mb-4">
                  <label className="enter-role-text">ESOPs Allocation (Annual ₹)</label>
                  <input type="number" className="date-label-input tasKCategory1" style={{ fontSize: '14px', height: '38px', border: '1px solid #ced4da' }} value={esop} onChange={(e) => setEsop(e.target.value)} />
                  <span className="badge mt-1" style={{ background: '#6c757d', color: '#fff', fontSize: '0.7rem' }}>No max cap (Taxable at exercise)</span>
                </div>
                <div className="col-lg-4 col-md-6 mb-4">
                  <label className="enter-role-text">Gift / Festival Vouchers (Annual ₹)</label>
                  <input type="number" className="date-label-input tasKCategory1" style={{ fontSize: '14px', height: '38px', border: '1px solid #ced4da' }} value={giftVoucher} onChange={(e) => setGiftVoucher(e.target.value)} />
                  <span className="badge mt-1" style={{ background: '#198754', color: '#fff', fontSize: '0.7rem' }}>Tax-free up to ₹5,000/yr</span>
                </div>
                <div className="col-lg-4 col-md-6 mb-4">
                  <label className="enter-role-text">Meal / Digital Allowances (Annual ₹)</label>
                  <input type="number" className="date-label-input tasKCategory1" style={{ fontSize: '14px', height: '38px', border: '1px solid #ced4da' }} value={mealVoucher} onChange={(e) => setMealVoucher(e.target.value)} />
                  <span className="badge mt-1" style={{ background: '#198754', color: '#fff', fontSize: '0.7rem' }}>Tax-free up to ₹26,400/yr</span>
                </div>
                <div className="col-lg-4 col-md-6 mb-4">
                  <label className="enter-role-text">Flexible Wellness Funds (Annual ₹)</label>
                  <input type="number" className="date-label-input tasKCategory1" style={{ fontSize: '14px', height: '38px', border: '1px solid #ced4da' }} value={flexFund} onChange={(e) => setFlexFund(e.target.value)} />
                  <span className="badge mt-1" style={{ background: '#6c757d', color: '#fff', fontSize: '0.7rem' }}>No statutory cap (Fully Taxable)</span>
                </div>
              </div>

              {isOver50Percent && (
                <div className="alert alert-danger mt-2">
                  <h6 className="fw-bold text-danger mb-2">⚠ 50% Cap Rule Violation Risk</h6>
                  <p className="small mb-2">
                    <strong>The 50% Cap:</strong> Under the 2026 Labour Codes, Basic Pay must make up at least 50% of the total CTC.
                  </p>
                  <p className="small mb-0">
                    <strong>Penalty:</strong> Excess non-basic allowances are added back to core wages, inflating PF and Gratuity liabilities.
                  </p>
                </div>
              )}

              {totalRewardsValue > 0 && !isOver50Percent && (
                <div className="alert alert-warning mt-2 py-2 text-center fw-semibold">
                  Running Total Optimized: ₹{totalRewardsValue.toLocaleString()} / year
                </div>
              )}
            </div>
          )}

          {/* ── STEP 4: Final Payslip ── */}
          {currentStep === 4 && (
            <div>
              <div className="sub-header-role mb-3" style={{ borderBottom: '1px solid #eeecff' }}>
                <h6 className="mt-2" style={{ color: '#53b7e8' }}>✓ Structure Generated — Compliant with 2026 Labour Codes</h6>
              </div>

              <div className="row g-3">
                {/* Employee Payslip */}
                <div className="col-md-6">
                  <div className="p-3 rounded h-100" style={{ border: '1px solid #d1fae5', background: '#f0fdf4' }}>
                    <h6 className="fw-bold mb-3 text-uppercase" style={{ color: '#166534', fontSize: '0.8rem', letterSpacing: '0.05em' }}>Employee Payslip (Monthly)</h6>

                    {[
                      { label: <TermTooltip title="Basic Salary" text="Core component. Must be ≥ 50% of core remuneration." />, value: basic },
                      { label: <TermTooltip title="HRA" text="House Rent Allowance. Metro: 50% of Basic, Non-Metro: 40%." />, value: hra },
                      { label: <TermTooltip title="Special Allowance" text="Balancing figure to meet target Gross." position="bottom" />, value: specialAllowance },
                    ].map((row, i) => (
                      <div key={i} className="d-flex justify-content-between mb-2" style={{ fontSize: '0.875rem' }}>
                        <span className="text-muted">{row.label}</span>
                        <span className="fw-semibold">₹{row.value.toFixed(0)}</span>
                      </div>
                    ))}

                    <div className="d-flex justify-content-between fw-bold pt-2 mb-3" style={{ borderTop: '1px solid #bbf7d0', fontSize: '0.875rem' }}>
                      <span>Total Gross Salary</span>
                      <span>₹{grossSalary.toFixed(0)}</span>
                    </div>

                    <div className="mb-2" style={{ fontSize: '0.75rem', fontWeight: '600', color: '#dc3545', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Deductions</div>
                    {[
                      { label: 'Employee PF', value: employeePF },
                      { label: 'Employee ESI', value: employeeESI },
                      { label: 'Professional Tax', value: pt },
                      { label: 'Employee LWF', value: employeeLWF },
                      { label: 'Projected Monthly TDS', value: monthlyTDS },
                    ].map((row, i) => (
                      <div key={i} className="d-flex justify-content-between mb-1 text-muted" style={{ fontSize: '0.8rem' }}>
                        <span>{row.label}</span><span>₹{row.value.toFixed(0)}</span>
                      </div>
                    ))}

                    <div className="d-flex justify-content-between align-items-center mt-3 p-2 rounded" style={{ background: '#dcfce7', borderTop: '1px solid #bbf7d0' }}>
                      <span className="fw-bold" style={{ color: '#166534' }}>NET TAKE-HOME</span>
                      <span className="fw-bold" style={{ color: '#166634', fontSize: '1.2rem' }}>₹{netSalary.toFixed(0)}</span>
                    </div>

                    <div className="mt-2 text-center text-muted" style={{ fontSize: '0.78rem' }}>
                      + Annual Statutory Bonus: <strong>{statutoryBonusMonthly > 0 ? `₹${(statutoryBonusMonthly * 12).toFixed(0)}` : '₹0 (Basic > ₹21k)'}</strong>
                    </div>

                    {totalRewardsValue > 0 && (
                      <div className="mt-3 pt-2" style={{ borderTop: '1px solid #bbf7d0' }}>
                        <div className="mb-1 fw-semibold" style={{ fontSize: '0.75rem', color: '#0e7490', textTransform: 'uppercase' }}>Tax-Efficient Benefits (Monthly Eqv.)</div>
                        {bonus > 0 && <div className="d-flex justify-content-between text-muted mb-1" style={{ fontSize: '0.8rem' }}><span>Performance Bonus</span><span>₹{(bonus / 12).toFixed(0)}</span></div>}
                        {esop > 0 && <div className="d-flex justify-content-between text-muted mb-1" style={{ fontSize: '0.8rem' }}><span>ESOPs Allocation</span><span>₹{(esop / 12).toFixed(0)}</span></div>}
                        {giftVoucher > 0 && <div className="d-flex justify-content-between text-muted mb-1" style={{ fontSize: '0.8rem' }}><span>Gift Vouchers</span><span>₹{(giftVoucher / 12).toFixed(0)}</span></div>}
                        {mealVoucher > 0 && <div className="d-flex justify-content-between text-muted mb-1" style={{ fontSize: '0.8rem' }}><span>Meal / Digital Allowances</span><span>₹{(mealVoucher / 12).toFixed(0)}</span></div>}
                        {flexFund > 0 && <div className="d-flex justify-content-between text-muted mb-1" style={{ fontSize: '0.8rem' }}><span>Flex Wellness Funds</span><span>₹{(flexFund / 12).toFixed(0)}</span></div>}
                      </div>
                    )}
                  </div>
                </div>

                {/* Employer CTC Breakdown */}
                <div className="col-md-6">
                  <div className="p-3 rounded h-100" style={{ border: '1px solid #e2dffe', background: '#f8f7ff' }}>
                    <h6 className="fw-bold mb-3 text-uppercase" style={{ color: '#53b7e8', fontSize: '0.8rem', letterSpacing: '0.05em' }}>Employer CTC (Monthly)</h6>

                    <div className="d-flex justify-content-between mb-3" style={{ fontSize: '0.875rem' }}>
                      <span className="text-muted">Gross Salary Paid</span>
                      <span>₹{grossSalary.toFixed(0)}</span>
                    </div>

                    <div className="mb-2" style={{ fontSize: '0.75rem', fontWeight: '600', color: '#53b7e8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Statutory Costs</div>
                    {[
                      { label: 'Employer PF', value: employerPF },
                      { label: 'Employer ESI', value: employerESI },
                      { label: 'EDLI & Admin', value: edli },
                      { label: 'Gratuity Provision', value: gratuity },
                      { label: 'Employer LWF', value: employerLWF },
                    ].map((row, i) => (
                      <div key={i} className="d-flex justify-content-between mb-1" style={{ fontSize: '0.8rem' }}>
                        <span className="text-muted">{row.label}</span><span>₹{row.value.toFixed(0)}</span>
                      </div>
                    ))}
                    <div className="d-flex justify-content-between mb-3" style={{ fontSize: '0.8rem' }}>
                      <span className={statutoryBonusMonthly === 0 ? "text-muted" : ""}>
                        <TermTooltip
                          title={`Mandatory Statutory Bonus (${statutoryBonusPct}%)`}
                          text={<span><strong>Applicability:</strong> Mandatory for employees with Basic ≤ ₹21,000/mo.<br /><br /><strong>Not Applicable:</strong> If basic salary is above limit, shows ₹0 (Basic &gt; ₹21k).</span>}
                          position="top"
                        />
                      </span>
                      <span className={statutoryBonusMonthly === 0 ? "text-muted" : ""}>₹{statutoryBonusMonthly.toFixed(0)}</span>
                    </div>

                    {totalRewardsValue > 0 ? (
                      <div className="p-2 rounded mb-2" style={{ background: '#ede9fe', fontSize: '0.8rem' }}>
                        <div className="d-flex justify-content-between text-muted mb-1"><span>Core Monthly CTC</span><span>₹{targetMonthlyCoreCtc.toFixed(0)}</span></div>
                        <div className="d-flex justify-content-between text-muted mb-1"><span>Tax-Efficient Rewards</span><span>₹{(totalRewardsValue / 12).toFixed(0)}</span></div>
                        <div className="d-flex justify-content-between fw-bold pt-1" style={{ borderTop: '1px solid #c4b5fd' }}>
                          <span>TOTAL MONTHLY CTC</span>
                          <span style={{ color: '#53b7e8' }}>₹{(ctc / 12).toFixed(0)}</span>
                        </div>
                      </div>
                    ) : (
                      <div className="d-flex justify-content-between fw-bold p-2 rounded" style={{ background: '#ede9fe', fontSize: '0.875rem' }}>
                        <span>TOTAL MONTHLY CTC</span>
                        <span style={{ color: '#53b7e8' }}>₹{(ctc / 12).toFixed(0)}</span>
                      </div>
                    )}

                    {totalRewardsValue > 0 && (
                      <div className="mt-3 p-2 rounded" style={{ background: '#fffbeb', border: '1px solid #fde68a', fontSize: '0.8rem' }}>
                        <div className="fw-semibold mb-1" style={{ color: '#92400e' }}>Tax-Efficient Exclusions</div>
                        By optimizing <strong>₹{(totalRewardsValue / 12).toFixed(0)}/mo</strong> outside the wage definition, approx.{' '}
                        <strong>₹{savedStatutory.toFixed(0)}/mo</strong> in PF/Gratuity liabilities avoided.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Footer Navigation */}
          <div className="d-flex justify-content-between mt-4 pt-3" style={{ borderTop: '1px solid #eeecff' }}>
            <button
              className="modal-create-btn"
              style={{ background: '#fff', color: '#53b7e8', border: '1px solid #53b7e8' }}
              onClick={currentStep === 4 ? resetWizard : prevStep}
              disabled={currentStep === 1}
            >
              {currentStep === 4 ? 'Start Over' : '← Back'}
            </button>

            {currentStep < totalSteps ? (
              <button
                className="modal-create-btn"
                onClick={nextStep}
                disabled={currentStep === 3 && isOver50Percent}
              >
                {currentStep === 3 ? 'Generate Payslip & Structure' : 'Next Step →'}
              </button>
            ) : (
              <button
                className="modal-create-btn"
                onClick={() => alert('Salary structure applied to payroll system successfully!')}
              >
                Apply to Payroll
              </button>
            )}
          </div>

        </div>
      </div>
    </div>
  );
};

export default SalaryStructureWizard;
